import { resolveLocale } from '@powerful/config/i18n';
import { SEARCH_AVAILABILITY, SEARCH_ESTABLISHMENT_TYPES } from '@powerful/types';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { countActiveFilters, type DiscoveryFilters } from '../../lib/discovery/search-params';
import { CategoryChips } from './category-chips';
import { OverlayDialog } from './overlay-dialog';

/**
 * Filtros da descoberta (canvas `ResultsFilters`, C1/C7/C8, R13).
 *
 * O MESMO formulário serve dois invólucros: sheet modal no mobile (rascunho + Aplicar) e painel
 * fixo de 280 px no desktop (aplica ao mudar). Só filtros que a Edge suporta — nada de controlo
 * sem efeito. Rádios e caixas NATIVOS por baixo dos chips: setas, espaço e leitor de ecrã
 * funcionam sem ARIA inventado. Preço tem campos numéricos e atalhos, nunca só slider.
 */
export const DISTANCE_OPTIONS = [1, 5, 10, 25, 50] as const;
export const RATING_OPTIONS = [4, 4.5] as const;
export const LANGUAGE_OPTIONS = [
  { code: 'pt', label: 'Português' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
] as const;
export const PRICE_SHORTCUTS = [
  { key: 'upTo25', min: undefined, max: 2500 },
  { key: 'from25to50', min: 2500, max: 5000 },
  { key: 'over50', min: 5000, max: undefined },
] as const;

/** «Limpar» mantém o que não é filtro: termo, cidade e ordenação. */
export function clearFilters(filters: DiscoveryFilters): DiscoveryFilters {
  return {
    ...(filters.query ? { query: filters.query } : {}),
    ...(filters.cityId ? { cityId: filters.cityId } : {}),
    ...(filters.sort ? { sort: filters.sort } : {}),
  };
}

/** «25», «25,5» ou «25.50» € → cêntimos. Vazio = sem limite; inválido = `null`. */
export function eurosToCents(raw: string): number | undefined | null {
  const value = raw.trim().replace(',', '.');
  if (!value) return undefined;
  if (!/^\d{1,6}(?:\.\d{1,2})?$/.test(value)) return null;
  return Math.round(Number(value) * 100);
}

const centsToEuros = (cents: number | undefined) => (cents === undefined ? '' : String(cents / 100).replace('.', ','));

function toggle<T extends string>(list: readonly T[] | undefined, item: T): T[] | undefined {
  const next = list?.includes(item) ? list.filter((x) => x !== item) : [...(list ?? []), item];
  return next.length ? next : undefined;
}

function ChipOption({ type, name, checked, onChange, children, lang }: {
  type: 'radio' | 'checkbox';
  name: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
  lang?: string;
}) {
  return (
    <label className="hit pc-chipopt" lang={lang}>
      <input className="pc-vh" type={type} name={name} checked={checked} onChange={onChange} />
      <span className={checked ? 'chip-cat on' : 'chip-cat'}>{children}</span>
    </label>
  );
}

export interface FilterFormProps {
  value: DiscoveryFilters;
  onChange: (next: DiscoveryFilters) => void;
  withCategories: boolean;
  hasOrigin: boolean;
  onChooseLocation: () => void;
  /** `change`: rascunho da sheet. `blur`: painel que aplica — não gerar histórico a cada tecla. */
  priceCommit: 'change' | 'blur';
  onValidityChange?: (valid: boolean) => void;
}

export function FilterForm({ value, onChange, withCategories, hasOrigin, onChooseLocation, priceCommit, onValidityChange }: FilterFormProps) {
  const { t, i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const oneDecimal = new Intl.NumberFormat(locale, { minimumFractionDigits: 1 });
  const kilometres = new Intl.NumberFormat(locale, { style: 'unit', unit: 'kilometer' });
  const uid = useId();
  const [minText, setMinText] = useState(centsToEuros(value.priceMinCents));
  const [maxText, setMaxText] = useState(centsToEuros(value.priceMaxCents));

  // Back/Forward ou «Limpar» trocam o valor por fora: os campos acompanham.
  useEffect(() => setMinText(centsToEuros(value.priceMinCents)), [value.priceMinCents]);
  useEffect(() => setMaxText(centsToEuros(value.priceMaxCents)), [value.priceMaxCents]);

  const min = eurosToCents(minText);
  const max = eurosToCents(maxText);
  const priceError =
    min === null || max === null
      ? t('discovery.filters.priceInvalid')
      : min !== undefined && max !== undefined && min > max
        ? t('discovery.filters.priceOrder')
        : null;

  useEffect(() => onValidityChange?.(priceError === null), [onValidityChange, priceError]);

  const set = (patch: Partial<DiscoveryFilters>) => {
    const next = { ...value, ...patch };
    for (const key of Object.keys(patch) as (keyof DiscoveryFilters)[]) if (next[key] === undefined) delete next[key];
    onChange(next);
  };

  const commitPrice = (minT = minText, maxT = maxText) => {
    const a = eurosToCents(minT);
    const b = eurosToCents(maxT);
    if (a === null || b === null || (a !== undefined && b !== undefined && a > b)) return;
    if (a === value.priceMinCents && b === value.priceMaxCents) return;
    set({ priceMinCents: a, priceMaxCents: b });
  };

  const errorId = `${uid}-price-err`;
  const n = (key: string) => `${uid}-${key}`;

  return (
    <div className="pc-form">
      {withCategories ? (
        <fieldset className="pc-fs">
          <legend>{t('discovery.filters.category')}</legend>
          <CategoryChips
            variant="wrap"
            label={t('discovery.filters.category')}
            selected={value.categoryIds ?? []}
            onToggle={(id) => set({ categoryIds: toggle(value.categoryIds, id) })}
          />
        </fieldset>
      ) : null}

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.distance')}</legend>
        {hasOrigin ? (
          <div className="pc-wrap">
            <ChipOption type="radio" name={n('radius')} checked={value.radiusKm === undefined} onChange={() => set({ radiusKm: undefined })}>
              {t('discovery.filters.anyDistance')}
            </ChipOption>
            {DISTANCE_OPTIONS.map((km) => (
              <ChipOption key={km} type="radio" name={n('radius')} checked={value.radiusKm === km} onChange={() => set({ radiusKm: km })}>
                {kilometres.format(km)}
              </ChipOption>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <p className="pc-muted pc-fs-hint">{t('discovery.filters.distanceNeedsOrigin')}</p>
            <button className="hit pc-textbtn" type="button" aria-haspopup="dialog" onClick={onChooseLocation}>
              {t('discovery.location.choose')}
            </button>
          </div>
        )}
      </fieldset>

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.price')}</legend>
        <div className="pc-price-in">
          {([
            ['min', minText, setMinText] as const,
            ['max', maxText, setMaxText] as const,
          ]).map(([key, text, setText]) => (
            <div className="field" key={key}>
              <label htmlFor={n(`p${key}`)}>{t(`discovery.filters.price_${key}`)}</label>
              <div className={priceError ? 'input is-error' : 'input'}>
                <input
                  id={n(`p${key}`)}
                  inputMode="decimal"
                  autoComplete="off"
                  value={text}
                  placeholder={key === 'min' ? '0' : '—'}
                  aria-invalid={priceError ? true : undefined}
                  aria-describedby={priceError ? errorId : undefined}
                  onChange={(event) => {
                    setText(event.target.value);
                    if (priceCommit === 'change') {
                      if (key === 'min') commitPrice(event.target.value, maxText);
                      else commitPrice(minText, event.target.value);
                    }
                  }}
                  onBlur={() => priceCommit === 'blur' && commitPrice()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && priceCommit === 'blur') commitPrice();
                  }}
                />
                <span aria-hidden="true" className="text-ink-3">€</span>
              </div>
            </div>
          ))}
        </div>
        {priceError ? <p className="pc-err" id={errorId} role="alert">{priceError}</p> : null}
        <div className="pc-wrap" role="group" aria-label={t('discovery.filters.priceShortcuts')}>
          {PRICE_SHORTCUTS.map((s) => {
            const on = value.priceMinCents === s.min && value.priceMaxCents === s.max;
            return (
              <button
                key={s.key}
                className="hit"
                type="button"
                aria-pressed={on}
                onClick={() => (on ? set({ priceMinCents: undefined, priceMaxCents: undefined }) : set({ priceMinCents: s.min, priceMaxCents: s.max }))}
              >
                <span className={on ? 'chip-cat on' : 'chip-cat'}>{t(`discovery.filters.shortcuts.${s.key}`)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.rating')}</legend>
        <div className="pc-wrap">
          <ChipOption type="radio" name={n('rating')} checked={value.minRating === undefined} onChange={() => set({ minRating: undefined })}>
            {t('discovery.filters.anyRating')}
          </ChipOption>
          {RATING_OPTIONS.map((r) => (
            <ChipOption key={r} type="radio" name={n('rating')} checked={value.minRating === r} onChange={() => set({ minRating: r })}>
              {t('discovery.filters.ratingAtLeast', { value: oneDecimal.format(r) })}
            </ChipOption>
          ))}
        </div>
      </fieldset>

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.availability')}</legend>
        <div className="pc-wrap">
          <ChipOption type="radio" name={n('avail')} checked={value.availability === undefined} onChange={() => set({ availability: undefined })}>
            {t('discovery.filters.anyTime')}
          </ChipOption>
          {SEARCH_AVAILABILITY.map((a) => (
            <ChipOption key={a} type="radio" name={n('avail')} checked={value.availability === a} onChange={() => set({ availability: a })}>
              {t(`discovery.filters.availabilityOptions.${a}`)}
            </ChipOption>
          ))}
        </div>
        <p className="pc-muted pc-fs-hint">{t('discovery.filters.availabilityHint')}</p>
      </fieldset>

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.languages')}</legend>
        <div className="pc-wrap">
          {LANGUAGE_OPTIONS.map((l) => (
            <ChipOption
              key={l.code}
              type="checkbox"
              name={n('lang')}
              lang={l.code}
              checked={value.languages?.includes(l.code) ?? false}
              onChange={() => set({ languages: toggle(value.languages, l.code) })}
            >
              {l.label}
            </ChipOption>
          ))}
        </div>
      </fieldset>

      <fieldset className="pc-fs">
        <legend>{t('discovery.filters.type')}</legend>
        <div className="pc-wrap">
          {SEARCH_ESTABLISHMENT_TYPES.map((type) => (
            <ChipOption
              key={type}
              type="checkbox"
              name={n('type')}
              checked={value.types?.includes(type) ?? false}
              onChange={() => set({ types: toggle(value.types, type) })}
            >
              {t(`discovery.filters.types.${type}`)}
            </ChipOption>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

export interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  value: DiscoveryFilters;
  onApply: (next: DiscoveryFilters) => void;
  hasOrigin: boolean;
  onChooseLocation: () => void;
}

/** Mobile: rascunho isolado — fechar sem aplicar não muda nada (nem a URL). */
export function FilterSheet({ open, onClose, value, onApply, hasOrigin, onChooseLocation }: FilterSheetProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [valid, setValid] = useState(true);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const active = countActiveFilters(draft);

  return (
    <OverlayDialog
      open={open}
      onClose={onClose}
      title={t('discovery.filters.title')}
      testId="filter-sheet"
      footer={
        <>
          <button className="btn btn-ghost" type="button" onClick={() => setDraft(clearFilters(draft))}>
            {t('discovery.filters.clear')}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              // Preço inválido: o erro já está visível e anunciado; o foco vai para o campo.
              if (!valid) {
                document.querySelector<HTMLInputElement>('[data-testid="filter-sheet"] [aria-invalid="true"]')?.focus();
                return;
              }
              onApply(draft);
              onClose();
            }}
          >
            {active > 0 ? t('discovery.filters.applyCount', { count: active }) : t('discovery.filters.apply')}
          </button>
        </>
      }
    >
      <FilterForm
        value={draft}
        onChange={setDraft}
        withCategories
        hasOrigin={hasOrigin}
        onChooseLocation={() => {
          onClose();
          onChooseLocation();
        }}
        priceCommit="change"
        onValidityChange={setValid}
      />
    </OverlayDialog>
  );
}

export interface FilterPanelProps {
  value: DiscoveryFilters;
  onApply: (next: DiscoveryFilters) => void;
  withCategories: boolean;
  hasOrigin: boolean;
  onChooseLocation: () => void;
}

/** Desktop (≥1024): painel fixo de 280 px que aplica ao mudar — o feed ao lado responde. */
export function FilterPanel({ value, onApply, withCategories, hasOrigin, onChooseLocation }: FilterPanelProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const active = countActiveFilters(value);
  return (
    <aside className="pc-aside pc-filters" aria-labelledby={titleId} data-testid="filter-panel">
      <div className="pc-sec-head">
        <h2 className="pc-h2" id={titleId}>{t('discovery.filters.title')}</h2>
        {active > 0 ? (
          <button className="hit pc-textbtn" type="button" onClick={() => onApply(clearFilters(value))}>
            {t('discovery.filters.clear')}
          </button>
        ) : null}
      </div>
      <FilterForm
        value={value}
        onChange={onApply}
        withCategories={withCategories}
        hasOrigin={hasOrigin}
        onChooseLocation={onChooseLocation}
        priceCommit="blur"
      />
    </aside>
  );
}
